import { useState } from 'react';
import { Activity, BookOpen, Braces, Check, Clapperboard, Copy, Hash, Play, Quote, Tag, Users, X } from 'lucide-react';
import type { AtomType, NewsAtom } from '@/lib/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { persistenceMode, repo } from '@/lib/repo';
import { cn } from '@/lib/utils';

// Extra seconds of pre-roll / post-roll shown around a clip so the cut has room
// to breathe — the preview starts HANDLE before the in-point and ends HANDLE
// after the out-point.
const HANDLE = 1;

// "17:05" / "01:59:54:13" → seconds (frames ignored). Used to preview clip ranges.
function tcToSec(tc?: string): number | undefined {
  if (!tc) return undefined;
  const parts = tc.trim().split(':').map(Number);
  if (parts.some((n) => Number.isNaN(n))) return undefined;
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  if (parts.length >= 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  return undefined;
}

const TYPE_META: Record<AtomType, { icon: typeof Activity; label: string }> = {
  event: { icon: Activity, label: 'Event' },
  fact: { icon: Check, label: 'Fact' },
  quote: { icon: Quote, label: 'Quote / SOT' },
  entity: { icon: Users, label: 'Entity' },
  statistic: { icon: Hash, label: 'Statistic' },
  media: { icon: Clapperboard, label: 'Media' },
  context: { icon: BookOpen, label: 'Context' },
};

function confidenceVariant(c: number) {
  if (c >= 0.8) return 'success' as const;
  if (c >= 0.6) return 'warning' as const;
  return 'danger' as const;
}

export function AtomCard({
  atom,
  onApprove,
  onReject,
}: {
  atom: NewsAtom;
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
}) {
  const meta = TYPE_META[atom.atom_type];
  const Icon = meta.icon;
  const anchors = atom.media_anchor;
  const corroborated = anchors.length > 1;
  const status = atom.review_process.status;
  const confidence = atom.review_process.confidence;
  const confidenceReason = atom.review_process.confidence_reason;

  const [showJson, setShowJson] = useState(false);
  const [copied, setCopied] = useState(false);
  const json = JSON.stringify(atom, null, 2);

  async function copyJson() {
    try {
      await navigator.clipboard.writeText(json);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  }

  const previewAnchor = anchors.find((a) => (a.media_type === 'video' || a.media_type === 'audio') && a.source_id);
  const canPreview = persistenceMode === 'cloud' && !!previewAnchor;
  const [preview, setPreview] = useState<{ url: string; type: string; start?: number; end?: number } | null>(null);
  const [previewBusy, setPreviewBusy] = useState(false);

  async function openPreview() {
    if (!previewAnchor) return;
    setPreviewBusy(true);
    try {
      const { url } = await repo.mediaUrl(atom.topic_id, previewAnchor.source_id);
      const type = previewAnchor.media_type === 'audio' ? 'audio' : 'video';
      const inPt = tcToSec(previewAnchor.timecode);
      const outPt = tcToSec(previewAnchor.timecode_end);
      // Pad the clip with a 1s handle on each side (in-point clamped at 0).
      const start = inPt != null ? Math.max(0, inPt - HANDLE) : undefined;
      const end = outPt != null ? outPt + HANDLE : undefined;
      setPreview({ url, type, start, end });
    } catch {
      setPreview({ url: '', type: 'error' });
    } finally {
      setPreviewBusy(false);
    }
  }

  return (
    <div
      className={cn(
        'rounded-lg border bg-card p-4 shadow-sm transition-colors',
        status === 'approved' && 'border-emerald-500/40',
        status === 'rejected' && 'opacity-50',
      )}
    >
      {/* What it is */}
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <Badge variant="info">
          <Icon className="mr-1 h-3 w-3" />
          {meta.label}
        </Badge>
        <Badge variant="secondary" className="capitalize">
          {atom.knowledge_frame.replace('_', ' ')}
        </Badge>
        {corroborated && <Badge variant="success">Corroborated · {anchors.length} sources</Badge>}
        <span className="group/conf relative ml-auto">
          <Badge variant={confidenceVariant(confidence)} className={confidenceReason ? 'cursor-help' : undefined}>
            {Math.round(confidence * 100)}% confidence
          </Badge>
          {confidenceReason && (
            <span
              role="tooltip"
              className={cn(
                'pointer-events-none absolute right-0 top-full z-20 mt-1.5 hidden w-64 rounded-md border border-border',
                'bg-popover p-2 text-[11px] leading-snug text-popover-foreground shadow-md group-hover/conf:block',
              )}
            >
              <span className="mb-0.5 block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Why this score
              </span>
              {confidenceReason}
            </span>
          )}
        </span>
      </div>

      {/* Payload (News Atom `statement`) */}
      <p className="text-sm text-foreground">{atom.statement}</p>

      {/* Classification: entities + tags */}
      {(atom.entities.length > 0 || atom.tags.length > 0) && (
        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          {atom.entities.map((e) => (
            <span
              key={e.name}
              className="inline-flex items-center rounded-full border border-border bg-muted px-2 py-0.5 text-[11px] text-muted-foreground"
            >
              <Tag className="mr-1 h-3 w-3" />
              {e.name}
            </span>
          ))}
          {atom.tags.map((t) => (
            <span key={t} className="text-[11px] text-muted-foreground">
              #{t}
            </span>
          ))}
        </div>
      )}

      {/* Provenance (News Atom `media_anchor`) — where this came from */}
      <div className="mt-3 border-t border-border pt-2">
        <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Where this came from</p>
        <ul className="space-y-0.5">
          {anchors.map((p, i) => (
            <li key={`${p.source_id}-${i}`} className="font-mono text-[11px] text-muted-foreground">
              {p.source_title ?? p.source_id}
              {p.external_id ? ` · ${p.external_id}` : ''}
              {p.timecode ? ` · @${p.timecode}` : ''}
            </li>
          ))}
        </ul>
      </div>

      {/* Review gate + raw JSON */}
      <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1">
        {status === 'approved' ? (
          <Badge variant="success">
            <Check className="mr-1 h-3 w-3" />
            Approved
          </Badge>
        ) : status === 'rejected' ? (
          <Badge variant="danger">Rejected</Badge>
        ) : (
          <>
            <Button size="sm" className="h-7 px-2 text-xs" onClick={() => onApprove(atom.atom_id)}>
              <Check className="mr-1 h-3.5 w-3.5" />
              Approve
            </Button>
            <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => onReject(atom.atom_id)}>
              <X className="mr-1 h-3.5 w-3.5" />
              Reject
            </Button>
          </>
        )}
        <span className="flex items-center gap-1">
          {canPreview && (
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-xs"
              disabled={previewBusy}
              onClick={() => (preview ? setPreview(null) : void openPreview())}
            >
              <Play className="mr-1 h-3.5 w-3.5" />
              {previewBusy ? '…' : preview ? 'Hide' : 'Preview'}
            </Button>
          )}
          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setShowJson((v) => !v)}>
            <Braces className="mr-1 h-3.5 w-3.5" />
            {showJson ? 'Hide JSON' : 'JSON'}
          </Button>
        </span>
      </div>

      {preview &&
        (preview.type === 'error' ? (
          <p className="mt-2 text-[11px] text-red-600">Preview unavailable — no stored media file for this source.</p>
        ) : preview.type === 'audio' ? (
          <audio
            className="mt-2 w-full"
            controls
            autoPlay
            src={preview.url}
            onLoadedMetadata={(e) => {
              e.currentTarget.currentTime = preview.start ?? 0;
            }}
            onSeeked={(e) => {
              // some browsers reset to 0 on first play; re-assert the in-point once
              if (preview.start && e.currentTarget.currentTime < preview.start - 0.5) {
                e.currentTarget.currentTime = preview.start;
              }
            }}
            onPlay={(e) => {
              // Pressing play after we paused at the out-point would re-trigger the
              // pause immediately — rewind to the in-point so replay works.
              if (preview.end != null && e.currentTarget.currentTime >= preview.end - 0.05) {
                e.currentTarget.currentTime = preview.start ?? 0;
              }
            }}
            onTimeUpdate={(e) => {
              if (preview.end != null && e.currentTarget.currentTime >= preview.end) e.currentTarget.pause();
            }}
          />
        ) : (
          <video
            className="mt-2 w-full rounded-md border border-border"
            controls
            autoPlay
            playsInline
            src={preview.url}
            onLoadedMetadata={(e) => {
              e.currentTarget.currentTime = preview.start ?? 0;
            }}
            onSeeked={(e) => {
              // some browsers reset to 0 on first play; re-assert the in-point once
              if (preview.start && e.currentTarget.currentTime < preview.start - 0.5) {
                e.currentTarget.currentTime = preview.start;
              }
            }}
            onPlay={(e) => {
              // Pressing play after we paused at the out-point would re-trigger the
              // pause immediately — rewind to the in-point so replay works.
              if (preview.end != null && e.currentTarget.currentTime >= preview.end - 0.05) {
                e.currentTarget.currentTime = preview.start ?? 0;
              }
            }}
            onTimeUpdate={(e) => {
              if (preview.end != null && e.currentTarget.currentTime >= preview.end) e.currentTarget.pause();
            }}
          />
        ))}

      {showJson && (
        <div className="mt-2">
          <div className="mb-1 flex justify-end">
            <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px]" onClick={copyJson}>
              <Copy className="mr-1 h-3 w-3" />
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
          <pre className="max-h-72 overflow-auto rounded-md border border-border bg-muted p-3 font-mono text-[11px] leading-relaxed">
            {json}
          </pre>
        </div>
      )}
    </div>
  );
}
